import type { StateCreator } from 'zustand/vanilla';

import type { GroupSP, UserSP } from '@/features/sharing-profile/types/sharing-profile.interface';

import type { SharingStore } from '../../store';
import type { AvailableSpaceUser } from './initial-state';
import { initialUserManagementState } from './initial-state';

export interface SharingUserManagementAction {
  /**
   * Replace the full list of users assigned to the sharing profile.
   */
  setUsers: (users: UserSP[]) => void;
  /**
   * Append a user to the assigned list. No-op if a user with the same email already exists.
   */
  addUser: (user: UserSP) => void;
  /**
   * Remove an assigned user by email.
   */
  removeUser: (email: string) => void;
  /**
   * Replace the full list of groups assigned to the sharing profile.
   */
  setGroups: (groups: GroupSP[]) => void;
  /**
   * Append a group to the assigned list. No-op if a group with the same id already exists.
   */
  addGroup: (group: GroupSP) => void;
  /**
   * Remove an assigned group by id.
   */
  removeGroup: (id: string) => void;
  /**
   * Set the users available in the current space to pick from (e.g. after fetching).
   */
  setAvailableUsers: (availableUsers: AvailableSpaceUser[]) => void;
  /**
   * Toggle the loading flag while fetching/updating available space users.
   */
  setUserManagementLoading: (isUserManagementLoading: boolean) => void;
  /**
   * Reset this slice's state back to its initial values.
   */
  resetUserManagement: () => void;
}

export const createUserManagementSlice: StateCreator<SharingStore, [['zustand/devtools', never]], [], SharingUserManagementAction> = (
  set,
  get,
) => ({
  setUsers: (users: UserSP[]) => {
    set({ users });
  },

  addUser: (user: UserSP) => {
    const { users } = get();
    if (users.some((existing) => existing.email === user.email)) {
      return;
    }
    set({ users: [...users, user] });
  },

  removeUser: (email: string) => {
    const { users } = get();
    set({ users: users.filter((user) => user.email !== email) });
  },

  setGroups: (groups: GroupSP[]) => {
    set({ groups });
  },

  addGroup: (group: GroupSP) => {
    const { groups } = get();
    if (groups.some((existing) => existing.id === group.id)) {
      return;
    }
    set({ groups: [...groups, group] });
  },

  removeGroup: (id: string) => {
    const { groups } = get();
    set({ groups: groups.filter((group) => group.id !== id) });
  },

  setAvailableUsers: (availableUsers: AvailableSpaceUser[]) => {
    set({ availableUsers });
  },

  setUserManagementLoading: (isUserManagementLoading: boolean) => {
    set({ isUserManagementLoading });
  },

  resetUserManagement: () => {
    set({ ...initialUserManagementState });
  },
});
